﻿import { useMemo, useState } from "react";
import { AdminButton, AdminPagination, AdminSearchInput, AdminStatusBadge, AdminTable, AdminTd, AdminTr } from "@/components/admin";
import { resolveAdminStatusDisplay } from "@/lib/adminCodeDisplay";
import { formatDateTime } from "@/lib/format";
import type { EmergencyEventItem } from "@/types.ts";
interface EmergencyListProps {
    events: EmergencyEventItem[];
    selectedId: number | null;
    onSelect: (id: number) => void;
    searchQuery: string;
    onSearchChange: (v: string) => void;
    activeFilter: string;
    totalCount: number;
    onRefresh: () => void;
}
const eventTypeLabel = (event: EmergencyEventItem) => {
    return resolveAdminStatusDisplay({
        group: "EMERGENCY_EVENT_TYPE",
        code: event.eventType,
        name: event.eventTypeName,
        shortName: event.eventTypeShortName,
        severity: event.eventTypeSeverity,
        fallbackTone: "red",
    }).label;
};
const eventStatusDisplay = (event: EmergencyEventItem) => {
    return resolveAdminStatusDisplay({
        group: "EMERGENCY_EVENT_STATUS",
        code: event.eventStatus,
        name: event.eventStatusName,
        shortName: event.eventStatusShortName,
        severity: event.eventSeverity,
        fallbackTone: "slate",
    });
};
const riskDisplay = (riskLevel: string) => {
    return resolveAdminStatusDisplay({
        group: "EMERGENCY_RISK_LEVEL",
        code: riskLevel,
        fallbackTone: "slate",
    });
};
const EmergencyList = ({ events, selectedId, onSelect, searchQuery, onSearchChange, activeFilter, totalCount, onRefresh, }: EmergencyListProps) => {
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;
    const filtered = useMemo(() => {
        let result = [...events];
        if (activeFilter === "open") {
            result = result.filter((event) => event.eventStatus === "OPEN");
        }
        else if (activeFilter === "in-progress") {
            result = result.filter((event) => ["ACKNOWLEDGED", "IN_PROGRESS"].includes(event.eventStatus));
        }
        else if (activeFilter === "resolved") {
            result = result.filter((event) => event.eventStatus === "RESOLVED");
        }
        else if (activeFilter === "sos") {
            result = result.filter((event) => event.eventType.includes("SOS") || event.eventType.includes("EMERGENCY"));
        }
        else if (activeFilter === "no-response") {
            result = result.filter((event) => event.eventType.includes("NO_RESPONSE"));
        }
        else if (activeFilter === "risk-sound") {
            result = result.filter((event) => event.eventType.includes("SOUND") || event.eventType.includes("RISK"));
        }
        if (searchQuery.trim()) {
            const q = searchQuery.trim().toLowerCase();
            result = result.filter((event) => [event.elderlyName, event.seniorUserAccountId, event.deviceId, event.eventType, event.description]
                .filter((value) => value != null)
                .some((value) => String(value).toLowerCase().includes(q)));
        }
        return result;
    }, [events, activeFilter, searchQuery]);
    const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
    const safePage = Math.min(currentPage, totalPages);
    const startIdx = (safePage - 1) * pageSize;
    const endIdx = Math.min(startIdx + pageSize, filtered.length);
    const pageItems = filtered.slice(startIdx, endIdx);
    return (<div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-list-check text-slate-500 text-sm"/>
          </div>
          <h3 className="text-sm font-semibold text-slate-700">긴급 이벤트 목록</h3>
          <span className="text-xs text-slate-400">총 {totalCount}건</span>
          {activeFilter !== "all" && (<span className="text-xs text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">필터 적용 중</span>)}
        </div>
        <div className="flex items-center gap-2">
          <AdminSearchInput value={searchQuery} onChange={(value) => {
            onSearchChange(value);
            setCurrentPage(1);
          }} placeholder="이름, ID, 기기, 유형 검색" className="w-56"/>
          <AdminButton type="button" variant="secondary" size="sm" icon="ri-refresh-line" onClick={onRefresh}>새로고침</AdminButton>
        </div>
      </div>

      <AdminTable headers={["어르신", "유형", "위험도", "처리 상태", "발생 시각", "설명"]} emptyMessage={pageItems.length === 0 ? "표시할 긴급 이벤트가 없습니다." : undefined}>
            {pageItems.map((event) => {
            const isSelected = event.eventId === selectedId;
            return (<AdminTr key={event.eventId} className={`cursor-pointer ${isSelected ? "bg-blue-50/60" : "hover:bg-slate-50/60"}`} onClick={() => onSelect(event.eventId)}>
                    <AdminTd>
                      <div className="flex flex-col gap-0.5">
                        <span className="text-xs font-semibold text-slate-800">{event.elderlyName}</span>
                        <span className="text-xs text-slate-400 shrink-0">ID {event.seniorUserAccountId}</span>
                      </div>
                    </AdminTd>
                    <AdminTd>
                      <span className="text-xs text-slate-600">{eventTypeLabel(event)}</span>
                    </AdminTd>
                    <AdminTd>
                      <AdminStatusBadge label={riskDisplay(event.riskLevel).label} tone={riskDisplay(event.riskLevel).tone}/>
                    </AdminTd>
                    <AdminTd>
                      <AdminStatusBadge label={eventStatusDisplay(event).label} tone={eventStatusDisplay(event).tone}/>
                    </AdminTd>
                    <AdminTd>
                      <span className="text-xs text-slate-500 whitespace-nowrap">{formatDateTime(event.occurredAt)}</span>
                    </AdminTd>
                    <AdminTd>
                      <span className="text-xs text-slate-500 truncate max-w-[10rem] block" title={event.description ?? ""}>
                        {event.description ?? event.eventTypeDescription ?? "-"}
                      </span>
                    </AdminTd>
                  </AdminTr>);
        })}
      </AdminTable>

      <AdminPagination currentPage={safePage} totalPages={totalPages} totalCount={filtered.length} pageSize={pageSize} onPageChange={setCurrentPage}/>
    </div>);
};
export default EmergencyList;
